import { Component, computed, effect, EventEmitter, inject, Injector, Input, model, OnChanges, OnInit, Output, signal, SimpleChanges, viewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, shareReplay, take, tap } from 'rxjs/operators';
import { AppUser } from 'src/app/shared/models/app-user';
import { AppSettingsDto, FilterDto, ListDashboardDto, ListVisualDto } from '../../../services/api.services';
import { InsightsLibraryDashboard, InsightsLibraryPage, InsightsLibraryVisual } from '../../state/insights-library';
import { FilterUtils } from 'src/app/pbir/utils/FilterUtils';
import { DataModelResolver } from 'src/app/pbir/dataModel/DataModelResolver';
import { toSignal } from '@angular/core/rxjs-interop';
import { AppCurrencyService } from 'src/app/app/AppCurrencyService';
import { SidePanelFilterListMode, SidePanelFilterModel } from 'src/app/pbir/components/SidePanelFilterList.component';
import { DashboardEmbedComponent } from 'src/app/pbir/components/DashboardEmbed.component';
import { VisualInteractions } from 'src/app/pbir/visuals/VisualInteractions';

@Component({
  selector: 'app-insights-library',
  templateUrl: './insights-library.component.html',
  styleUrls: ['./insights-library.component.scss'],
  standalone: false
})
export class InsightsLibraryComponent implements OnInit, OnChanges {
  readonly currencyService = inject(AppCurrencyService);
  readonly injector = inject(Injector);

  @Input() user: AppUser;

  @Input() loadingApp = false;
  @Input() loadingItems = false;
  @Input() busy = false;

  @Input() appSettings: AppSettingsDto;

  @Input() loadingDashboards = false;
  @Input() dashboards: ListDashboardDto[] = [];

  @Input() loadingVisuals = false;
  @Input() visuals: ListVisualDto[] = [];

  @Input() saving = false;


  @Input() page = InsightsLibraryPage.Insights;
  @Input() starredOnly = false;
  @Input() filter = '';


  @Input() inViewMode = false;
  @Input() selectedVisual: InsightsLibraryVisual;
  @Input() selectedDashboard: InsightsLibraryDashboard;


  @Output("close") _close = new EventEmitter();
  @Output("setPage") _setPage = new EventEmitter<InsightsLibraryPage>();
  @Output("setStarredOnly") _setStarredOnly = new EventEmitter<boolean>();
  @Output("setFilter") _setFilter = new EventEmitter<string>();

  @Output('turnViewModeOff') _turnViewModeOff = new EventEmitter();

  @Output() dashboardCategorySelected = new EventEmitter<any>();
  @Output() dashboardItemSelected = new EventEmitter<any>();

  @Output() visualCategorySelected = new EventEmitter<any>();
  @Output() visualItemSelected = new EventEmitter<any>();

  @Output() favouriteDashboard = new EventEmitter<InsightsLibraryDashboard>();
  @Output() viewDashboard = new EventEmitter<InsightsLibraryDashboard>();
  @Output() copyDashboard = new EventEmitter<InsightsLibraryDashboard>();
  @Output() toggleDashboardHome = new EventEmitter<InsightsLibraryDashboard>();
  @Output() exportToPDFDashboard = new EventEmitter<{ item: InsightsLibraryDashboard }>();

  @Output() favouriteVisual = new EventEmitter<InsightsLibraryVisual>();
  @Output() addToHomeVisual = new EventEmitter<InsightsLibraryVisual>();
  @Output() viewVisual = new EventEmitter<InsightsLibraryVisual>();
  @Output() copyVisual = new EventEmitter<InsightsLibraryVisual>();

  filterTextControl = new FormControl('');

  viewedItemName = '';
  canCopyViewedItem = false;
  canExportToPDFViewedItem = false;

  readonly dataModelResovler = inject(DataModelResolver);
  readonly dataModel = toSignal(this.dataModelResovler.getOrganisationModel());

  readonly loadingAppSignal = signal(false);
  readonly loading = computed(() => this.loadingAppSignal() || !this.currencyService.loaded());

  readonly SidePanelFilterListMode = SidePanelFilterListMode;

  readonly filters = model<ReadonlyArray<SidePanelFilterModel>>([]);
  readonly filterDtos = computed(() => this.filters().map(x => x.dto));

  readonly dashboardEmbed = viewChild<DashboardEmbedComponent>('dashboardEmbed');
  readonly visualHosts = computed(() => this.dashboardEmbed()?.visualEmbeds()?.map(x => x.visualHost()));
  readonly visualInteractions = new VisualInteractions(this.injector, this.visualHosts);

  setFilters() {
    let item = this.selectedDashboard ?? this.selectedVisual;
    let filters = item?.filters.map(x => ({
      id: SidePanelFilterModel.generateId(),
      dto: x,
      collapsed: false,
      disabled: false,
    })) ?? [];

    this.filters.set(filters);
  }

  onFiltersChanged() { }
  
  constructor() {
    this.currencyService.ensureLoaded();

    this.filterTextControl.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap(s => this.setFilter(s))
    ).subscribe();
  }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.loadingApp)
      this.loadingAppSignal.set(this.loadingApp);

    if (changes.busy) {
      if (this.busy) {
        this.filterTextControl.disable();
      }
      else {
        this.filterTextControl.enable();
      }
    }

    this.checkViewedItemChanges(changes);

    if (changes.selectedDashboard || changes.selectedVisual)
      this.setFilters();
  }

  private checkViewedItemChanges(changes: SimpleChanges) {
    if (this.page === InsightsLibraryPage.Insights) {
      if (changes.page || changes.selectedVisual) {
        this.viewedItemName = this.selectedVisual?.name;
        this.canCopyViewedItem = this.selectedVisual?.canCopy ?? false;
        this.canExportToPDFViewedItem = false;
      }
    }
    else if (this.page === InsightsLibraryPage.Dashboards) {
      if (changes.page || changes.selectedDashboard) {
        this.viewedItemName = this.selectedDashboard?.name;
        this.canCopyViewedItem = this.selectedDashboard?.canCopy ?? false;
        this.canExportToPDFViewedItem = true;
      }
    }
  }


  close() {
    this._close.emit();
  }

  setPage(page: InsightsLibraryPage | string) {
    if (page === this.page)
      return;

    this._setPage.emit(<InsightsLibraryPage>page);
  }

  toggleStarredOnly() {
    this._setStarredOnly.emit(!this.starredOnly);
  }

  setFilter(value: string) {
    value = (value ?? '').trim();

    if (value === this.filter)
      return;

    this._setFilter.emit(value);
  }


  turnViewModeOff() {
    this._turnViewModeOff.emit();
  }

  copyViewedItem() {
    if (this.page === InsightsLibraryPage.Insights) {
      this.copyVisual.emit(this.selectedVisual);
    }
    else if (this.page === InsightsLibraryPage.Dashboards) {
      this.copyDashboard.emit(this.selectedDashboard);
    }
  }

  exportToPDFViewedItem() {
    if (this.page === InsightsLibraryPage.Dashboards) {
      this.exportToPDFDashboard.emit({
        item: this.selectedDashboard
      });
    }
  }
}
